import { getCognitoIdToken, hasStoredCognitoSession } from "@src/appV2/Auth/cognito/cognitoAuth";
import { tokenFailureMetadata } from "@src/appV2/Auth/loginRum";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";

export const COGNITO_TOKEN_REQUEST_SOURCE = {
  axiosInterceptor: "axios-interceptor",
  superagentHelper: "superagent-helper",
} as const;

type CognitoTokenRequestSource =
  (typeof COGNITO_TOKEN_REQUEST_SOURCE)[keyof typeof COGNITO_TOKEN_REQUEST_SOURCE];

interface GetCognitoTokenForRequestParams {
  source?: CognitoTokenRequestSource;
}

const TOKEN_UNAVAILABLE_ERROR_MESSAGE =
  "Aborting authenticated request: stored Cognito session present but token unavailable";

// Empty token + stored session -> retry with `forceRefresh: true`, then throw
// if still empty so the request never fires. Otherwise the unauthenticated
// request would 401 and could force logout/error flows for a user whose session
// is actually intact. Returning `undefined` is reserved for genuinely signed-out
// users (no `LastAuthUser` key).
export async function getCognitoTokenForRequest({
  source = COGNITO_TOKEN_REQUEST_SOURCE.axiosInterceptor,
}: GetCognitoTokenForRequestParams = {}): Promise<string | undefined> {
  try {
    const token = await getCognitoIdToken();
    if (typeof token === "string" && token.length > 0) {
      return token;
    }
  } catch (error: unknown) {
    // The force-refresh retry below recovers the vast majority of these
    // first-attempt failures (Datadog staff-1324: ~91% never reach a retry
    // failure), after which the request proceeds normally. Emitting a
    // `logError` here therefore floods Error Tracking with timeouts that were
    // ultimately recovered. Record a non-error action instead and only
    // escalate to `logError` when the retry itself fails (a real failure the
    // retry did not recover). Token-refresh control flow is unchanged.
    logEvent(
      APP_V2_APP_EVENTS.GET_COGNITO_TOKEN_TRANSIENT_FAILURE,
      tokenFailureMetadata(error, source)
    );
  }

  if (!hasStoredCognitoSession()) {
    return undefined;
  }

  let retryError: unknown;
  try {
    const refreshed = await getCognitoIdToken(true);
    if (typeof refreshed === "string" && refreshed.length > 0) {
      return refreshed;
    }
  } catch (error: unknown) {
    retryError = error;
  }

  const tokenUnavailableError = new Error(TOKEN_UNAVAILABLE_ERROR_MESSAGE);
  const loggedError = retryError ?? tokenUnavailableError;
  logError(APP_V2_APP_EVENTS.GET_COGNITO_TOKEN_FAILURE, {
    error: loggedError,
    metadata: { ...tokenFailureMetadata(loggedError, source), retried: true },
  });

  throw tokenUnavailableError;
}
